// Romanian B2B billing contract (BOLT ticket BP). One contract on the existing
// endpoint URLs: no contractVersion fields, no version query parameters.

export type BusinessAction =
    | 'EDIT_CUI'
    | 'RETRY_LOOKUP'
    | 'SWITCH_TO_INDIVIDUAL'
    | 'CONTINUE_WITHOUT_BILLING'
    | 'RESTART_FROM_TERMINAL';

export type BusinessRejectionReason =
    | 'FISCALLY_INACTIVE'
    | 'REGISTRATION_SUSPENDED'
    | 'REGISTRATION_DISSOLVED'
    | 'REGISTRATION_STRUCK_OFF';

export interface BusinessCompanyStatus {
    registrationStatus:
        | 'registered'
        | 'suspended'
        | 'dissolved'
        | 'struck_off'
        | 'unknown';
    registrationStatusRaw: string | null;
    fiscalStatus: 'active' | 'inactive' | 'unknown';
}

export interface VerifiedBusiness {
    entityType: 'business';
    name: string;
    tax: {
        countryCode: 'RO';
        cui: string;
        vatRegistered: boolean;
        invoiceIdentifier: string;
    };
    address: {
        countryCode: 'RO';
        region: string;
        city: string;
        line1: string;
        line2?: string;
        postalCode?: string;
        rawAddress: string;
        source: 'anaf';
        addressKind: 'fiscal' | 'registered_office';
    };
}

export interface BusinessVerification {
    reference: string;
    provider: 'anaf';
    checkedAt: string;
    effectiveDate: string;
    expiresAt: string;
}

interface BusinessResponseBase {
    requestId: string;
    allowedActions: BusinessAction[];
}

export interface BusinessLookupEligible extends BusinessResponseBase {
    operation: 'lookup';
    outcome: 'eligible';
    canSubmitBusiness: true;
    company: VerifiedBusiness;
    companyStatus: BusinessCompanyStatus & {
        registrationStatus: 'registered';
        fiscalStatus: 'active';
    };
    verification: BusinessVerification;
    error?: never;
}

export interface BusinessSubmitSuccess extends BusinessResponseBase {
    operation: 'submit';
    outcome: 'submitted';
    canSubmitBusiness: false;
    allowedActions: [];
    submission: { reference: string; submittedAt: string };
    error?: never;
}

/** HTTP status is transport metadata, not duplicated in the JSON body. */
export const BUSINESS_ERROR_RULES = {
    CUI_INVALID_FORMAT: { outcome: 'invalid', lookupHttp: 400, submitHttp: 400, field: 'cui', retryable: false },
    CUI_NOT_FOUND: { outcome: 'not_found', lookupHttp: 200, submitHttp: 422, field: 'cui', retryable: false },
    COMPANY_INELIGIBLE: { outcome: 'ineligible', lookupHttp: 200, submitHttp: 422, field: 'cui', retryable: false },
    ANAF_UNAVAILABLE: { outcome: 'unavailable', lookupHttp: 503, submitHttp: 503, field: null, retryable: true },
    ANAF_TIMEOUT: { outcome: 'unavailable', lookupHttp: 504, submitHttp: 504, field: null, retryable: true },
    ANAF_RESPONSE_INVALID: { outcome: 'unavailable', lookupHttp: 502, submitHttp: 502, field: null, retryable: true },
    COMPANY_STATUS_UNVERIFIED: { outcome: 'unverified', lookupHttp: 200, submitHttp: 422, field: null, retryable: false },
    COMPANY_DATA_INCOMPLETE: { outcome: 'unverified', lookupHttp: 200, submitHttp: 422, field: null, retryable: false },
    LOOKUP_RATE_LIMITED: { outcome: 'unavailable', lookupHttp: 429, submitHttp: 429, field: null, retryable: true },
    BILLING_SESSION_EXPIRED: { outcome: 'session_error', lookupHttp: 410, submitHttp: 410, field: null, retryable: false },
    BILLING_SESSION_UNAUTHORIZED: { outcome: 'session_error', lookupHttp: 401, submitHttp: 401, field: null, retryable: false },
    BILLING_SESSION_FORBIDDEN: { outcome: 'session_error', lookupHttp: 403, submitHttp: 403, field: null, retryable: false },
    VERIFICATION_STALE: { outcome: 'conflict', lookupHttp: 409, submitHttp: 409, field: 'cui', retryable: false },
    BILLING_REQUEST_INVALID: { outcome: 'invalid', lookupHttp: 400, submitHttp: 400, field: null, retryable: false },
    BILLING_INTERNAL_ERROR: { outcome: 'error', lookupHttp: 500, submitHttp: 500, field: null, retryable: true },
} as const;

export type BusinessErrorCode = keyof typeof BUSINESS_ERROR_RULES;
type ErrorRule<C extends BusinessErrorCode> = typeof BUSINESS_ERROR_RULES[C];

type BusinessError<C extends BusinessErrorCode> = {
    code: C;
    field: ErrorRule<C>['field'];
    retryable: ErrorRule<C>['retryable'];
} & (C extends 'COMPANY_INELIGIBLE'
    ? { reasons: [BusinessRejectionReason, ...BusinessRejectionReason[]] }
    : { reasons: [] }) &
    (C extends 'LOOKUP_RATE_LIMITED'
        ? { retryAfterSeconds: number }
        : { retryAfterSeconds?: never });

export type BusinessFailure = {
    [C in BusinessErrorCode]: BusinessResponseBase & {
        operation: 'lookup' | 'submit';
        outcome: ErrorRule<C>['outcome'];
        canSubmitBusiness: false;
        error: BusinessError<C>;
        companyStatus?: BusinessCompanyStatus;
        company?: never;
        verification?: never;
    };
}[BusinessErrorCode];

export type BusinessResponse =
    | BusinessLookupEligible
    | BusinessSubmitSuccess
    | BusinessFailure;

/** Path supplies tenant/session token; authentication still uses verificationToken. */
export interface BusinessLookupRequest {
    verificationToken: string;
    taxCountryCode: 'RO';
    recipientType: 'BUSINESS';
    taxId: string;
}

export interface BusinessSubmitRequest {
    verificationToken: string;
    business: { cui: string; verificationReference: string };
    idempotencyKey: string;
}

/** Accepted submission identity returned by the authenticated session GET while terminal handoff is pending. */
export type BusinessSubmissionRecovery = Omit<BusinessSubmitRequest, 'verificationToken'>;

/**
 * Raw HTTP result of a business operation. The envelope is parsed from the
 * body of 200 and non-2xx responses alike; `body` is unvalidated (`unknown`).
 * Network failures reject instead of resolving.
 */
export interface BusinessTransportResult {
    status: number;
    body: unknown;
    retryAfterHeader?: string | null;
}
